import { tv, VariantProps } from "tailwind-variants";
import { Slot } from "@radix-ui/react-slot";
import { ButtonHTMLAttributes, forwardRef } from "react";

const button = tv({
  base: [
    "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold outline-none",
    "shadow-sm transition-[color,background-color,box-shadow,opacity,transform] duration-200 ease-out",
    "focus-visible:ring-2 focus-visible:ring-offset-2",
    "active:scale-[0.98] active:opacity-90",
    "disabled:pointer-events-none disabled:opacity-50",
  ],
  variants: {
    variant: {
      ghost:
        "rounded-md px-2 shadow-none hover:bg-zinc-100 focus-visible:ring-zinc-400 dark:hover:bg-white/5 dark:focus-visible:ring-zinc-500",
      primary:
        "bg-apcap text-white shadow-md hover:bg-apcap-light focus-visible:ring-apcap focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-950",
      /** Verde institucional — telas de auth e destaques */
      brand:
        "bg-apcap text-white shadow-md hover:bg-apcap-light focus-visible:ring-apcap focus-visible:ring-offset-2",
      outline:
        "border border-zinc-300 bg-white text-zinc-800 shadow-sm hover:bg-zinc-50 focus-visible:ring-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800",
      brandOutline:
        "border-2 border-apcap/40 bg-white text-apcap shadow-sm hover:bg-apcap-muted/50 focus-visible:ring-2 focus-visible:ring-apcap/35 focus-visible:ring-offset-2 dark:bg-zinc-900 dark:hover:bg-zinc-800/80",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { asChild, variant, className, type = "button", ...props },
    ref
  ) {
    const classes = button({ variant, className });

    if (asChild) {
      return <Slot ref={ref} {...props} className={classes} />;
    }

    return <button ref={ref} type={type} {...props} className={classes} />;
  }
);
